import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { AttachmentModel, UserRole, DeleteByIdPayload, RequestResult, UpsertAttachmentInput } from '../../../gql-types.generated';
import { selectError, clearError, captureDeleteTradingPartnerAttachmentStatus, captureUpsertTradingPartnerAttachmentsStatus, selectDeleteTradingPartnerAttachmentStatus, selectUpsertTradingPartnerAttachmentsStatus, selectAttachmentToDownload, clearPartnerAttachment } from '../../../features/TradingPartnerDetails/TradingPartnerDetailsSlice';
import { deleteTradingPartnerAttachment, fetchPartnerAttachment, upsertTradingPartnerAttachments } from '../../../features/TradingPartnerDetails/TradingPartnerDetailsActions';
import AttachmentList from './AttachmentList';
import { downloadBlobAsFile, getFileDataString, openBlobAsFileInNewTab } from '../../../util/Common';


interface PartnerAttachmentListProps {
    viewerRole: UserRole | undefined;
    partnerId: string;
    attachments: AttachmentModel[] | undefined;
    refreshPartnerData: () => void;
}

const TradingPartnerAttachmentList: React.FC<PartnerAttachmentListProps> = (props: PartnerAttachmentListProps) => {
    const { viewerRole, partnerId, attachments, refreshPartnerData } = props;
    const dispatch = useAppDispatch();
    const [deleteStatus, setDeleteStatus] = useState<DeleteByIdPayload | undefined>();
    const [addResult, setAddResult] = useState<RequestResult | undefined>();
    const [addResultMessage, setAddResultMessage] = useState<string | undefined>();
    const [openAttachment, setOpenAttachment] = useState<boolean | undefined>();

    const upsertTradingPartnerAttachmentStatus = useAppSelector(selectUpsertTradingPartnerAttachmentsStatus);
    const deleteTradingPartnerAttachmentStatus = useAppSelector(selectDeleteTradingPartnerAttachmentStatus);
    const attachmentToDownload = useAppSelector(selectAttachmentToDownload);
    const error = useAppSelector(selectError);


    useEffect(() => {
        // save off add result for base component
        if (upsertTradingPartnerAttachmentStatus && upsertTradingPartnerAttachmentStatus.result) {
            // set the result to be passed to base component
            setAddResult(upsertTradingPartnerAttachmentStatus.result);
            setAddResultMessage(upsertTradingPartnerAttachmentStatus.message as string);
        }
        else {
            setAddResult(undefined);
        }

        if (deleteTradingPartnerAttachmentStatus && deleteTradingPartnerAttachmentStatus.result) {
            // set the status to be passed to base component
            setDeleteStatus(deleteTradingPartnerAttachmentStatus);
        }
        else {
            setDeleteStatus(undefined);
        }
    }, [deleteTradingPartnerAttachmentStatus?.result, upsertTradingPartnerAttachmentStatus?.result]);

    useEffect(() => {
        if (attachmentToDownload && attachmentToDownload[0] && attachmentToDownload[0] && attachmentToDownload[0].fileName) {
            const attachment = attachmentToDownload[0];
            if (attachment.attachmentString !== undefined && attachment.attachmentString !== null && attachment.fileName && attachment.mimeType) {
                const fileData = getFileDataString(true, attachment.mimeType);
                fetch(fileData + "," + attachment.attachmentString)
                    .then(res => res.blob())
                    .then(blob => {
                        if (openAttachment) {
                            openBlobAsFileInNewTab(blob, attachment.fileName as string);
                        } else {
                            downloadBlobAsFile(blob, attachment.fileName as string);
                        }

                        dispatch(clearPartnerAttachment());
                        setOpenAttachment(undefined);
                    });
            }
        }
    }, [attachmentToDownload]);

    const onDeleteDialogClose = () => {
        // clear the delete status
        dispatch(captureDeleteTradingPartnerAttachmentStatus());
    };

    const onAttachmentsDialogClose = () => {
        // clear the upsert status
        dispatch(captureUpsertTradingPartnerAttachmentsStatus());
    };

    const onAttachmentsSave = (attachments: UpsertAttachmentInput[]) => {
        if (attachments && partnerId) {
            dispatch(
                upsertTradingPartnerAttachments(
                    partnerId,
                    attachments
                )
            );
        }
    };

    const onAttachmentDelete = (id: string) => {
        if (id) {
            dispatch(deleteTradingPartnerAttachment(id));
        }
    }

    const onAttachmentDownload = (id: string) => {
        if (id) {
            setOpenAttachment(false);
            dispatch(fetchPartnerAttachment(id));
        }
    }

    const onAttachmentOpen = (id: string) => {
        if (id) {
            setOpenAttachment(true);
            dispatch(fetchPartnerAttachment(id));
        }
    }

    const handleClearError = () => {
        dispatch(clearError());
    };

    return (
        <AttachmentList
            viewerRole={viewerRole}
            attachments={attachments}
            deleteAttachmentStatus={deleteStatus}
            upsertAttachmentResult={addResult}
            upsertAttachmentResultMessage={addResultMessage}
            deleteAttachment={onAttachmentDelete}
            downloadAttachment={onAttachmentDownload}
            openAttachment={onAttachmentOpen}
            onDeleteDialogClose={onDeleteDialogClose}
            onAttachmentsDialogClose={onAttachmentsDialogClose}
            saveAttachments={onAttachmentsSave}
            refreshList={refreshPartnerData}
            clearError={handleClearError}
            error={error}
        />

    );

}

export default TradingPartnerAttachmentList;